import React, { useState, useEffect, createRef, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "lib";
import { Button, TextField, Avatar } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { uploadSingleFile } from "utils/functions";
import { toast } from "react-toastify";
import { FileUploadWrapper } from "lib";
import { UsersAPI } from "api_inbusiness";
import DefaultUserPicture from "resources/img/user/DefaultUserPicture.png";
import styles from "./Profile.module.scss";
import { Cropper } from "react-cropper";
import "cropperjs/dist/cropper.css";

const ChangeProfilePicture = ({
  open,
  setOpen,
  userData,
  getUserById,
  userPhoto,
}) => {
  const { t } = useTranslation();
  const [fileBlob, setFileBlob] = useState(null);
  const [name, setName] = useState("");
  const [profilePic, setProfilePic] = useState("");
  const [image, setImage] = useState(null);
  const [cropData, setCropData] = useState(null);
  const cropperRef = useRef();

  const onSubmitFunc = () => {
    try {
      const data = handleCrop();
      if (!data) {
        toast.error("Nu s-a putut procesa imaginea. Încearcă din nou.");
        return;
      }
      
      UsersAPI.updateUserAvatar(userData.id, data).then((res) => {
        if (res.ok === true) {
          getUserById();
          setOpen(false);
          setName("");
          setImage(null);
          toast.success("Poza de profil a fost actualizată cu succes!");
        } else {
          console.error("Upload error:", res.error);
          toast.error("A intervenit o eroare la încărcare!");
        }
      }).catch((err) => {
        console.error("API call failed:", err);
        toast.error("Eroare de rețea. Verifică conexiunea.");
      });
    } catch (error) {
      console.error("handleCrop error:", error);
      toast.error("Eroare la procesarea imaginii!");
    }
  };

  const getCropData = () => {
    if (cropperRef && cropperRef.current !== undefined) {
      setCropData(cropperRef.current.cropper.getCroppedCanvas().toDataURL());
      toast.success("Imaginea a fost decupată cu succes!");
    }
  };

  const handleCrop = () => {
    try {
      const imageElement = cropperRef?.current;
      const cropper = imageElement?.cropper;
      
      if (!cropper) {
        console.error("Cropper not initialized");
        return null;
      }

      // Use smaller canvas size for iOS compatibility
      const canvas = cropper.getCroppedCanvas({
        maxWidth: 1024,
        maxHeight: 1024,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      
      if (!canvas) {
        console.error("Canvas creation failed");
        return null;
      }

      // Get base64 data as JPEG for better compatibility and smaller size
      let base64Data = canvas.toDataURL('image/jpeg', 0.9);

      // Get the content type and data
      let contentType = base64Data.split(";")[0].split(":")[1];
      let raw = window.atob(base64Data.split(",")[1]);

      // Create a Uint8Array from the base64 raw data
      let uint8Array = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; ++i) {
        uint8Array[i] = raw.charCodeAt(i);
      }
      
      let blob = new Blob([uint8Array], { type: contentType });

      // Create a FormData object and append the blob
      let formData = new FormData();
      formData.append("file", blob, "profile.jpg");

      return formData.get("file");
    } catch (error) {
      console.error("handleCrop error:", error);
      return null;
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type - accept common image formats including HEIC
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error("Format de imagine neacceptat. Folosește JPG, PNG, GIF sau WebP.");
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imaginea nu poate depăși 10MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
    };
    reader.onerror = () => {
      toast.error("Eroare la citirea fișierului. Încearcă din nou.");
    };
    reader.readAsDataURL(file);
  };

  return (
    <CustomDialog
      open={open}
      setOpen={setOpen}
      title={t("Schimbă poza de profil")}
      buttonClose={t("Înapoi")}
      buttonFinish={t("Finalizează")}
      onClickButtonFinish={onSubmitFunc}
      onClickButtonClose={() => {
        setName("");
        setImage(null);
        setOpen(false);
        cropperRef.current = null;
      }}
      width="650px"
      maxWidth="650px"
    >
      <div className={styles.changeContainer}>
        <div className={styles.changeLeft}>
          <div>
            <label htmlFor="file-upload" className={styles.customFileUpload}>
              Selectează o imagine nouă
            </label>
            <input 
              id="file-upload" 
              type="file" 
              accept="image/*"
              onChange={handleFileChange} 
            />
          </div>

          {(userPhoto || image) && (
            <Button
              variant="contained"
              component="label"
              onClick={getCropData}
              endIcon={<AttachFileIcon />}
              sx={{
                width: "252px",
                height: "52px",
                padding: "6px 12px",
                backgroundColor: "#5B80BA",
                borderRadius: "10px",
              }}
            >
              {"Decupează"}
            </Button>
          )}
        </div>
        <div className={styles.changeRight}>
          <Avatar
            src={userPhoto ? userPhoto : image ? image : DefaultUserPicture}
            alt={userData.firstName}
            sx={{
              width: "150px",
              height: "150px",
            }}
          ></Avatar>
          {image && (
            <div>
              <Cropper
                ref={cropperRef}
                style={{ height: 200, width: "100%", marginTop: "10px" }}
                // zoomTo={0.5}
                aspectRatio={800 / 800}
                initialAspectRatio={16 / 9}
                preview=".img-preview"
                src={image ? image : userPhoto}
                viewMode={1}
                minCropBoxWidth={800}
                minCropBoxHeight={800}
                // background={false}
                responsive={true}
                autoCropArea={1}
                autoCrop={true}
                // checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
                guides={false}
                crop={handleCrop}
                // onInitialized={onInitialized}
              />
            </div>
          )}
        </div>
        {cropData ? (
          <img
            src={cropData}
            alt="crop"
            style={{ width: "auto", height: "300px" }}
          />
        ) : (
          <></>
        )}
      </div>
    </CustomDialog>
  );
};

export default ChangeProfilePicture;
